import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from './Tooltip';
import './ui.css';

const cx = (...parts) => parts.filter(Boolean).join(' ');

/** Text button. `type="button"` by default so it never submits a form. */
export function Button({ variant = 'default', size, className, type = 'button', children, ...rest }) {
    return (
        // eslint-disable-next-line react/button-has-type
        <button type={type} className={cx('v2-btn', `v2-btn--${variant}`, size && `v2-btn--${size}`, className)} {...rest}>
            {children}
        </button>
    );
}

Button.propTypes = {
    variant: PropTypes.oneOf(['default', 'primary', 'ghost', 'danger', 'link']),
    size: PropTypes.oneOf(['sm']),
    className: PropTypes.string,
    type: PropTypes.string,
    children: PropTypes.node,
};

/** Square icon-only button; `label` is its accessible name and hover title. */
export function IconButton({ label, className, children, ...rest }) {
    return (
        <button type="button" className={cx('v2-ib', className)} aria-label={label} title={label} {...rest}>
            {children}
        </button>
    );
}

IconButton.propTypes = {
    label: PropTypes.string.isRequired,
    className: PropTypes.string,
    children: PropTypes.node,
};

/**
 * One-of-n toggle group (New / Used, density …); each option reports
 * aria-pressed. An option with a `hint` gets it as a tooltip on hover and
 * keyboard focus (and as the button's accessible description).
 */
export function SegmentedControl({ label, options, value, onChange, className }) {
    return (
        <div className={cx('v2-seg', className)} role="group" aria-label={label}>
            {options.map((o) => {
                const btn = (
                    <button
                        key={o.value}
                        type="button"
                        aria-pressed={o.value === value}
                        onClick={() => { if (o.value !== value) onChange(o.value); }}
                    >
                        {o.label}
                    </button>
                );
                return o.hint ? <Tooltip key={o.value} content={o.hint}>{btn}</Tooltip> : btn;
            })}
        </div>
    );
}

SegmentedControl.propTypes = {
    label: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        label: PropTypes.node.isRequired,
        hint: PropTypes.node,
    })).isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func.isRequired,
    className: PropTypes.string,
};

export const CloseIcon = () => (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" focusable="false">
        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
    </svg>
);
